import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabase/supabaseClient'

const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [hasActiveLicense, setHasActiveLicense] = useState(false);
  const [hadActiveLicense, setHadActiveLicense] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // Function to check user's subscription status
  const checkSubscriptionStatus = async (userEmail) => {
    // Prevent multiple simultaneous calls
    if (subscriptionLoading || !userEmail) {
      return;
    }

    try {
      setSubscriptionLoading(true);

      // Check for active subscription
      const { data: subscriptionData, error: subError } = await supabase
        .from('subscriptions')
        .select('status')
        .eq('customer_email', userEmail)
        .in('status', ['active', 'on_trial', 'past_due'])
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('  - ❌ Subscription database error:', subError);
      }

      // NEW: Track if they have an active subscription
      setHasActiveSubscription(!!subscriptionData);

      // If subscription is active, user has access
      if (subscriptionData) {
        setSubscriptionActive(true);
        return;
      }

      // Check for 24-hour licenses
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (authUser) {
        // Check if user has EVER had a license (expired or active)
        const { data: anyLicenseData, error: anyLicenseError } = await supabase
          .from('user_licenses')
          .select('expires_at')
          .eq('user_id', authUser.id)
          .limit(1)
          .maybeSingle();
        if (anyLicenseError) {
          console.error('  - ❌ Any license check error:', anyLicenseError);
        }

        // Set hadActiveLicense if they've ever had any license
        setHadActiveLicense(!!anyLicenseData);

        // Check for ACTIVE (non-expired) license
        const { data: licenseData, error: licenseError } = await supabase
          .from('user_licenses')
          .select('expires_at')
          .eq('user_id', authUser.id)
          .gt('expires_at', new Date().toISOString())
          .limit(1)
          .maybeSingle();
        if (licenseError) {
          console.error('  - ❌ Active license database error:', licenseError);
        }

        // Set hasActiveLicense if they currently have an active license
        setHasActiveLicense(!!licenseData);


        // If license is active and not expired, user has access
        if (licenseData) {
          setSubscriptionActive(true);
          return;
        }
      }


      // No active subscription or license found
      setSubscriptionActive(false);

    } catch (err) {
      console.error('  - 💥 Exception:', err);
      setSubscriptionActive(false);
    } finally {
      setSubscriptionLoading(false);
    }
  };


















  // nienke updating 3 nov so that you will not see a different hex color after logging in with google:
  const handleLoginRedirect = () => {
    const redirectPath = localStorage.getItem('redirectAfterLogin') ||
      sessionStorage.getItem('redirectAfterLogin') ||
      window.redirectAfterLogin;

    if (redirectPath) {
      // Clean up storage
      localStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      delete window.redirectAfterLogin;

      // Check if it's an absolute URL from a different domain
      try {
        const url = new URL(redirectPath, window.location.origin);
        const targetPath = url.pathname + url.search + url.hash;
        // Use history API instead of window.location to avoid reload
        window.history.replaceState({}, '', targetPath);
      } catch (e) {
        window.history.replaceState({}, '', redirectPath);
      }
    }
  };

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)

      if (!session?.user) {
        setSubscriptionActive(false)
      } else {
        // Trigger subscription check immediately when user is set
        checkSubscriptionStatus(session.user.email);
      }

      setLoading(false)
    }


    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)

        if (!session?.user) {
          setSubscriptionActive(false);
        } else {
          // Handle redirect FIRST, before other logic
          handleLoginRedirect();

          // Then trigger subscription check
          checkSubscriptionStatus(session.user.email);
        }

        setLoading(false)
      }
    )


    return () => subscription?.unsubscribe()
  }, [])

  useEffect(() => {
    if (user?.email) {
      checkSubscriptionStatus(user.email);
    } else {
      setSubscriptionActive(false);
      setSubscriptionLoading(false);
    }
  }, [user?.email]);

  // Function to manually refresh subscription status (useful after subscribing)
  const refreshSubscriptionStatus = async () => {
    if (user?.email) {
      await checkSubscriptionStatus(user.email)
    }
  }

  // Sign up with email and password (keeping for backward compatibility)
  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  }

  // Sign in with email and password (keeping for backward compatibility)
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  // Send verification code to email (works for both existing and new users)
  const sendVerificationCode = async (email) => {
    try {
      // Send OTP - this will work for both existing and new users
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true, // Allow creating new users
          emailRedirectTo: undefined, // Ensure OTP instead of magic link
        }
      })

      if (error) {
        return { error }
      }

      return { data, error: null }
    } catch (err) {
      return { error: { message: 'Failed to send verification code' } }
    }
  }

  // Verify the OTP code (works for both sign-in and sign-up)
  const verifyCode = async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    })
    return { data, error }
  }

  const signOut = async () => {
    try {
      // Try the official Supabase signOut first
      const { error } = await supabase.auth.signOut()

      if (!error) {
        // Official signOut worked - we're done!
        return { error: null }
      }

      // Check if session still exists after failed signOut
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        // Manual cleanup as fallback
        localStorage.removeItem('sb-hfizqqbythrxppxhsxed-auth-token')

        // Verify the session is now cleared
        const { data: { session: afterSession } } = await supabase.auth.getSession()

        if (!afterSession) {
          // Manual cleanup worked
          setUser(null)
          setSubscriptionActive(false)
          return { error: null }
        } else {
          // Even manual cleanup didn't work - this shouldn't happen
          console.error('❌ Manual cleanup failed - session still exists')
          return { error: new Error('Logout failed completely') }
        }
      } else {
        // Session was cleared despite the error - treat as success
        return { error: null }
      }

    } catch (err) {
      console.error('❌ Logout process threw an exception:', err)

      // Last resort fallback on exception
      try {
        localStorage.removeItem('sb-hfizqqbythrxppxhsxed-auth-token')
        setUser(null)
        setSubscriptionActive(false)
        return { error: null }
      } catch (cleanupErr) {
        console.error('❌ Even emergency cleanup failed:', cleanupErr)
        return { error: err }
      }
    }
  }


  // Sign in with Google
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin // This ensures we come back to the same domain
      }
    })
    return { data, error }
  }

  const value = {
    user,
    loading,
    subscriptionActive,
    subscriptionLoading,
    hasActiveLicense,
    hadActiveLicense,
    hasActiveSubscription,
    refreshSubscriptionStatus,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    sendVerificationCode,
    verifyCode,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}